import type { LucideProps } from 'lucide-react'
import * as LucideIcons from 'lucide-react'
import React from 'react'

type LucideIconComponent = React.ComponentType<LucideProps>

export type LucideIconName = {
  [K in keyof typeof LucideIcons]: (typeof LucideIcons)[K] extends LucideIconComponent
    ? K
    : never
}[keyof typeof LucideIcons]

interface LucideIconProps extends Omit<LucideProps, 'ref'> {
  name: LucideIconName
}

/**
 * Dynamic Icon component that renders any Lucide React icon by name
 * Designed for icon renderers like Prism which can display any icon but shouldn't have to import all icons.
 *
 * @param name - The name of the Lucide icon to render (e.g., "Home", "User", "Settings")
 * @param props - All standard Lucide icon props (size, color, strokeWidth, className, etc.)
 *
 * @example
 * <LucideIcon name="Home" size={24} />
 * <LucideIcon name="User" size={16} color="#666" strokeWidth={1.5} />
 * <LucideIcon name="Settings" className="my-icon" absoluteStrokeWidth />
 */
export const LucideIcon: React.FC<LucideIconProps> = ({ name, ...props }) => {
  const LucideIconComponent = (
    LucideIcons as unknown as Record<string, LucideIconComponent>
  )[name]
  if (!LucideIconComponent) return null
  return <LucideIconComponent {...props} />
}
